'use client'

import React, { forwardRef } from 'react'

import { Icon, IconSize } from '@sidecar-components/atoms/Icon'
import { Link, LinkType } from '@sidecar-components/atoms/Link'
import { buttonStyles } from './styles'
import { ButtonProps, ButtonSize, ButtonSentiment } from './Button.types'

// The icon size increases when the button increases
const iconSizeMap = {
  [ButtonSize.LARGE]: IconSize.MD,
  [ButtonSize.MEDIUM]: IconSize.SM,
  [ButtonSize.SMALL]: IconSize.XS,
}

// We need the forwardRef so Buttons can be modal/menu triggers
export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      busy,
      children = '',
      className = '',
      disabled = false,
      icon = '',
      iconPosition = 'after',
      label = '',
      linkComponent: LinkComponent,
      onClick,
      path,
      pathType = LinkType.INTERNAL,
      sentiment = ButtonSentiment.PRIMARY,
      size = ButtonSize.MEDIUM,
      width = 'flex',
      type,
      testId,
      ...rest
    }: ButtonProps,
    forwardedRef: any,
  ) => {
    const baseStyles = buttonStyles({
      busy,
      children,
      disabled,
      icon,
      sentiment,
      size,
      width,
    })

    const iconSize = iconSizeMap[size] || IconSize.SM

    // If an icon is passed, show it
    let iconComponent
    if (icon) {
      iconComponent = busy ? (
        // If busy, replace the icon with a spinning loading icon
        <Icon
          name="loading"
          ref={forwardedRef}
          size={iconSize}
          className="animate-spin"
        />
      ) : (
        <Icon name={icon} ref={forwardedRef} size={iconSize} />
      )
      // If no icon is passed, set it as null
    } else {
      iconComponent = null
    }

    // Icon and text that go inside the button wrapper
    // Button wrapper varies on if it's a link or button
    const buttonContent = (
      <>
        {iconPosition === 'before' && iconComponent}

        {/* Truncate makes sure the button doesn't get to wide. It replaces extra copy with an ... */}
        <span className="truncate">
          {/* Replace the button label with "Loading" only if its busy and there is no icon */}
          {busy && !icon ? 'Loading...' : children}
        </span>

        {iconPosition === 'after' && iconComponent}
      </>
    )

    // NextJS links that look like buttons
    if (path && LinkComponent)
      return (
        <LinkComponent
          aria-label={label}
          className={`${baseStyles} ${className}`}
          data-testid={testId}
          href={path}
          ref={forwardedRef}
          {...rest}
        >
          {buttonContent}
        </LinkComponent>
      )

    // Client-side links that look like buttons
    if (path)
      return (
        <Link
          aria-label={label}
          className={`${baseStyles} ${className}`}
          testId={testId}
          disabled={disabled || busy}
          path={path}
          type={pathType}
          // removeStyles prevents the Link styles from messing up the Button styles
          removeStyles={true}
          ref={forwardedRef}
          {...rest}
        >
          {buttonContent}
        </Link>
      )

    // Normal buttons
    return (
      <button
        aria-busy={busy}
        aria-label={label}
        className={`${baseStyles} ${className}`}
        data-testid={testId}
        disabled={disabled || busy}
        onClick={disabled || busy ? undefined : onClick}
        type={type}
        ref={forwardedRef}
        {...rest}
      >
        {buttonContent}
      </button>
    )
  },
)
